// These two sources are used to implement the seeded shuffle algorithm.
// https://stackoverflow.com/a/53758827
// https://stackoverflow.com/a/47593316/1016460

/** Yields random numbers between 0 and 1. */
export type RandomGenerator = Generator<number, never, never>;

/**
 * Shuffle an array using a random generator and the Fisher-Yates shuffle algorithm.
 * @param array - The array to shuffle.
 * @param rand - The random generator to use for the shuffle, defaults to Math.random()
 * @returns The shuffled array.
 */
export function shuffle<T>(array: T[], rand: RandomGenerator = mathRandom()): T[] {
  const shuffled = [...array];

  for (let i = 0; i < shuffled.length; i++) {
    const randomIndex = i + Math.floor(rand.next().value * (shuffled.length - i));
    [shuffled[i], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[i]];
  }
  return shuffled;
}

function* mathRandom(): RandomGenerator {
  while (true) {
    yield Math.random();
  }
}

/**
 * A simple PRNG that produces a number between 0 and 1.
 * @param seed - The seed to use for the PRNG.
 * @returns A generator that yields numbers between 0 and 1.
 */
export function* splitmix32(seed: number): RandomGenerator {
  while (true) {
    seed |= 0;
    seed = (seed + 0x9e3779b9) | 0;
    let t = seed ^ (seed >>> 16);
    t = Math.imul(t, 0x21f0aaad);
    t = t ^ (t >>> 15);
    t = Math.imul(t, 0x735a2d97);
    yield ((t = t ^ (t >>> 15)) >>> 0) / 4294967296;
  }
}

/**
 * Convert a number between 0 and 1 to a 32-bit integer.
 * @param normalizedNumber - A number between 0 and 1.
 * @returns A 32-bit integer.
 */
export function normalizedTo32BitInt(normalizedNumber: number): number {
  if (normalizedNumber < 0 || normalizedNumber > 1) {
    throw new Error(`Input must be between 0 and 1, got ${normalizedNumber}`);
  }
  return (normalizedNumber * 2 ** 32) >>> 0;
}

/**
 * Generate a new 32-bit seed
 * @returns A 32-bit integer.
 */
export function newSeed(): number {
  return normalizedTo32BitInt(Math.random());
}
